import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/assets";
import { User } from "lucide-react";

const PORTAL_URL = "https://admin.ecoleicdh.org";

const links = ["about", "why", "programs", "admissions", "news", "gallery", "contact"];
const mobileLinks = ["about", "why", "programs", "professional", "admissions", "news", "gallery", "alumni", "careers", "contact"];

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${scrolled ? "bg-icdh-white/85 backdrop-blur-xl border-b border-icdh-navy/10 py-3" : "bg-transparent py-5"}`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between">
        <a href="#top" onClick={go("top")} data-testid="nav-logo" className="flex items-center gap-3">
          <img src={LOGO} alt="ICDH" className="h-11 w-11 object-contain bg-icdh-white rounded-full p-1 shadow-sm" />
          <span className="leading-tight min-w-0 max-w-[55vw] sm:max-w-none">
            <span className={`block font-sans font-extrabold tracking-tight text-[13px] sm:text-sm md:text-base truncate sm:whitespace-nowrap ${scrolled ? "text-icdh-navy" : "text-icdh-white"}`}>Institution Chrétienne d'Haïti</span>
            <span className={`block font-serif italic text-xs sm:text-sm tracking-wide ${scrolled ? "text-icdh-red" : "text-icdh-white/95"}`}>{t.motto}</span>
          </span>
        </a>

        <nav className="hidden min-[1360px]:flex items-center gap-2.5">
          {links.map((l) => (
            <a key={l} href={`#${l}`} onClick={go(l)} data-testid={`nav-${l}`}
              className={`text-[13px] font-medium whitespace-nowrap transition-colors duration-300 hover:text-icdh-red ${scrolled ? "text-icdh-navy/85" : "text-icdh-white/90"}`}>
              {t.nav[l]}
            </a>
          ))}
          <Link to="/stem" data-testid="nav-stem"
            className={`text-[13px] font-semibold transition-colors duration-300 whitespace-nowrap ${scrolled ? "text-icdh-red hover:text-icdh-navy" : "text-icdh-white hover:text-icdh-red"}`}>
            {t.nav.stem}
          </Link>
          <a href={PORTAL_URL} data-testid="nav-portal-demo"
            className={`text-[13px] font-medium transition-colors duration-300 whitespace-nowrap hover:text-icdh-red ${scrolled ? "text-icdh-navy/85" : "text-icdh-white/90"}`}>
            {t.nav.portal_demo}
          </a>
          <a href="#donate" onClick={go("donate")} data-testid="nav-donate"
            className={`text-sm font-bold transition-colors duration-300 inline-flex items-center gap-1.5 whitespace-nowrap ${scrolled ? "text-icdh-red hover:text-icdh-navy" : "text-icdh-white hover:text-icdh-red"}`}>
            <span className="h-1.5 w-1.5 bg-icdh-red rounded-full animate-pulse" />{t.nav.donate}
          </a>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button onClick={toggle} data-testid="lang-toggle"
            className={`h-10 px-3 md:px-4 border text-xs font-bold tracking-[0.15em] transition-colors duration-300 ${scrolled ? "border-icdh-navy/25 text-icdh-navy hover:bg-icdh-navy hover:text-icdh-white" : "border-icdh-white/40 text-icdh-white hover:bg-icdh-white hover:text-icdh-navy"}`}>
            {lang === "fr" ? "EN" : "FR"}
          </button>

          {user ? (
            <Link to="/parents" data-testid="nav-portal"
              className="hidden sm:inline-flex h-10 items-center gap-2 px-4 border border-icdh-red text-icdh-red hover:bg-icdh-red hover:text-icdh-white text-xs font-bold tracking-[0.15em] uppercase transition-colors duration-300">
              <User size={14} />{t.nav.portal}
            </Link>
          ) : (
            <Link to="/login" data-testid="nav-login"
              className={`hidden sm:inline-flex h-10 items-center px-4 border text-xs font-bold tracking-[0.15em] uppercase transition-colors duration-300 ${scrolled ? "border-icdh-navy/25 text-icdh-navy hover:bg-icdh-navy hover:text-icdh-white" : "border-icdh-white/40 text-icdh-white hover:bg-icdh-white hover:text-icdh-navy"}`}>
              {t.nav.login}
            </Link>
          )}

          <a href="#admissions" onClick={go("admissions")} data-testid="nav-apply"
            className="hidden md:inline-flex h-10 items-center px-5 bg-icdh-red text-icdh-white text-xs font-bold tracking-[0.15em] uppercase hover:bg-icdh-navy transition-colors duration-300">
            {t.nav.apply}
          </a>

          <button onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle"
            className={`min-[1360px]:hidden h-10 w-10 flex items-center justify-center border ${scrolled ? "border-icdh-navy/25 text-icdh-navy" : "border-icdh-white/40 text-icdh-white"}`}>
            <span className="text-lg">{open ? "×" : "≡"}</span>
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="min-[1360px]:hidden bg-icdh-white border-t border-icdh-navy/10 px-6 py-5 flex flex-col gap-4">
          {mobileLinks.map((l) => (
            <a key={l} href={`#${l}`} onClick={go(l)} data-testid={`mobile-nav-${l}`} className="text-sm font-medium text-icdh-navy">{t.nav[l]}</a>
          ))}
          <Link to="/stem" onClick={() => setOpen(false)} data-testid="mobile-nav-stem" className="text-sm font-semibold text-icdh-red">{t.nav.stem}</Link>
          <a href={PORTAL_URL} data-testid="mobile-nav-portal-demo" className="text-sm font-medium text-icdh-navy">{t.nav.portal_demo}</a>
          <a href="#donate" onClick={go("donate")} data-testid="mobile-nav-donate" className="text-sm font-bold text-icdh-red">● {t.nav.donate}</a>
          <div className="pt-3 border-t border-icdh-navy/10 flex gap-3">
            {user ? (
              <Link to="/parents" onClick={() => setOpen(false)} data-testid="mobile-nav-portal" className="text-sm font-bold uppercase tracking-[0.15em] text-icdh-red">{t.nav.portal}</Link>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-nav-login" className="text-sm font-bold uppercase tracking-[0.15em] text-icdh-navy">{t.nav.login}</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
